<template>
    <div v-if="mostrar" class="modal-mask" @click="fechar">
        <div class="jm margem modal-repactuar" @click.stop>
            <div class="modal-repactuar-cabecalho">
                <h2>Repactuar fim previsto</h2>
                <p v-if="entregaNome" class="modal-repactuar-subtitulo">{{ entregaNome }}</p>
            </div>

            <div class="grid-1">
                <div>
                    <label for="repactuar-dt-fim">Novo fim previsto <span class="obrigatorio">*</span></label>
                    <input id="repactuar-dt-fim" v-model="form.dtFim" type="date" :min="dataMinima" required
                        @change="atualizarLinhaRepactuacao" />
                </div>

                <div v-if="temHistorico" class="bloco-historico-repactuacao">
                    <label for="repactuar-historico">Histórico de repactuações</label>
                    <textarea id="repactuar-historico" :value="observacaoRepactuacaoHistorico" rows="3" readonly
                        class="textarea-historico"></textarea>
                </div>

                <div>
                    <label for="repactuar-observacao">Observação da repactuação <span class="obrigatorio">*</span></label>
                    <textarea id="repactuar-observacao" v-model="form.observacaoLinhaNova" rows="4"
                        :readonly="!campoObservacaoAtivo" :class="{ 'textarea-desabilitada': !campoObservacaoAtivo }"
                        :placeholder="placeholderObservacao"></textarea>
                    <p v-if="!campoObservacaoAtivo" class="modal-repactuar-dica">
                        Altere o fim previsto para habilitar a observação.
                    </p>
                </div>
            </div>

            <p class="modal-repactuar-aviso">As alterações só serão gravadas ao clicar em Salvar na tabela.</p>

            <div class="submit direita modal-repactuar-acoes">
                <button type="button" class="acao-secundaria" @click="fechar">
                    Cancelar
                </button>
                <button type="button" :disabled="!formularioValido" :title="tituloSalvarDesabilitado"
                    @click="confirmar">
                    Confirmar
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    PREFIXO_LINHA_REPACTUACAO,
    dataRepactuacaoAlterada,
    juntarObservacaoRepactuacao,
    montarPrefixoLinhaRepactuacao,
    motivoRepactuacaoPreenchido,
} from '@/utils/sprintGestao'
import { dataParaInput } from '@/utils/date'

export default {
    name: 'ModalRepactuarEntrega',

    props: {
        mostrar: {
            type: Boolean,
            default: false,
        },
        entregaNome: {
            type: String,
            default: '',
        },
        fimPrevistoAtual: {
            type: String,
            default: null,
        },
        fimPrevistoReferencia: {
            type: String,
            default: null,
        },
        observacaoRepactuacaoHistorico: {
            type: String,
            default: '',
        },
        observacaoRepactuacaoLinhaPendente: {
            type: String,
            default: '',
        },
    },

    emits: ['fechar', 'salvar'],

    data() {
        return {
            form: {
                dtFim: '',
                observacaoLinhaNova: '',
            },
        }
    },

    computed: {
        dataMinima() {
            return new Date().toISOString().slice(0, 10)
        },

        temHistorico() {
            return Boolean(String(this.observacaoRepactuacaoHistorico || '').trim())
        },

        campoObservacaoAtivo() {
            return dataRepactuacaoAlterada(this.fimPrevistoReferencia, this.form.dtFim)
        },

        prefixoLinhaAtual() {
            return montarPrefixoLinhaRepactuacao(this.fimPrevistoReferencia, this.form.dtFim)
        },

        placeholderObservacao() {
            if (this.campoObservacaoAtivo) return ''
            return 'Disponível após alterar o fim previsto'
        },

        formularioValido() {
            return this.campoObservacaoAtivo
                && motivoRepactuacaoPreenchido(this.form.observacaoLinhaNova, this.prefixoLinhaAtual)
        },

        tituloSalvarDesabilitado() {
            if (this.formularioValido) return ''
            if (!this.campoObservacaoAtivo) {
                return 'Altere o fim previsto em relação ao valor atual'
            }
            return 'Complete o motivo da repactuação após o texto padrão'
        },
    },

    watch: {
        mostrar(aberto) {
            if (aberto) {
                this.inicializarFormulario()
            }
        },
    },

    methods: {
        inicializarFormulario() {
            this.form.dtFim = dataParaInput(this.fimPrevistoAtual)

            if (this.campoObservacaoAtivo) {
                const linhaPendente = String(this.observacaoRepactuacaoLinhaPendente || '').trim()
                this.form.observacaoLinhaNova = linhaPendente || this.prefixoLinhaAtual
            } else {
                this.form.observacaoLinhaNova = ''
            }
        },

        atualizarLinhaRepactuacao() {
            if (!this.campoObservacaoAtivo) {
                this.form.observacaoLinhaNova = ''
                return
            }

            const prefixoNovo = this.prefixoLinhaAtual
            const linhaAtual = this.form.observacaoLinhaNova
            const match = linhaAtual.match(PREFIXO_LINHA_REPACTUACAO)
            const sufixoMotivo = match && linhaAtual.length > match[0].length
                ? linhaAtual.slice(match[0].length)
                : ''

            this.form.observacaoLinhaNova = prefixoNovo + sufixoMotivo

            if (!sufixoMotivo && !linhaAtual) {
                this.form.observacaoLinhaNova = prefixoNovo
            }
        },

        fechar() {
            this.$emit('fechar')
        },

        confirmar() {
            if (!this.formularioValido) return

            const linhaNova = String(this.form.observacaoLinhaNova).trim()
            this.$emit('salvar', {
                dtFim: this.form.dtFim,
                observacao_repactuacao: juntarObservacaoRepactuacao(
                    this.observacaoRepactuacaoHistorico,
                    linhaNova,
                ),
            })
        },
    },
}
</script>

<style scoped>
.modal-repactuar {
    min-width: min(32rem, 92vw);
    max-width: 40rem;
}

.modal-repactuar-cabecalho {
    margin-bottom: 1rem;
}

.modal-repactuar-cabecalho h2 {
    margin: 0 0 0.35rem;
}

.modal-repactuar-subtitulo {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.8;
}

.modal-repactuar textarea {
    width: 100%;
    min-height: 6rem;
    resize: vertical;
}

.textarea-historico {
    min-height: 4.5rem;
    opacity: 0.85;
    background: var(--cor-cinza, #f5f5f5);
}

.textarea-desabilitada {
    opacity: 0.65;
    cursor: not-allowed;
    background: var(--cor-cinza, #f5f5f5);
}

.bloco-historico-repactuacao {
    margin-bottom: 0.25rem;
}

.modal-repactuar-dica {
    margin: 0.35rem 0 0;
    font-size: 0.82rem;
    color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.55));
}

.modal-repactuar-aviso {
    margin: 0 0 0.75rem;
    font-size: 0.85rem;
    color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.55));
}

.modal-repactuar-acoes {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.obrigatorio {
    color: var(--cor-erro, #c62828);
}
</style>
